import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Crosshair, Plus, Crown, Coins, CheckCircle, CaretDoubleRight, Trophy } from "@phosphor-icons/react";
import { fetchContent, trackEvent, FALLBACK_CONTENT_V2, FALLBACK_CONTENT_V3, FALLBACK_CONTENT_V4, FALLBACK_CONTENT_V5, FALLBACK_CONTENT_V6, FALLBACK_CONTENT_V7, FALLBACK_CONTENT_V1_HUD } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const THEMES = {
  green: { primary: "#6FF20A", amber: "#FFC63D", body: "#BCCCA1", ladderA: "#21E6E0", ladderB: "#FF2E88" },
  sunset: { primary: "#FF6A2C", amber: "#FFB020", body: "#E7D2BE", ladderA: "#FFB020", ladderB: "#FF3D57" },
  midnight: { primary: "#00F0FF", amber: "#FFE600", body: "#B5A6DB", ladderA: "#00F0FF", ladderB: "#FF007F" },
};
const RED = "#6FF20A";
const AMBER = "#FFC63D";
const BODY = "#BCCCA1";

function CountUp({ value, prefix = "$" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const dur = 1100;
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref}>
      {prefix}
      {n.toLocaleString()}
    </span>
  );
}

function TopBar({ brandLead, brandAccent, dates, rightNode, primary = RED }) {
  return (
    <header className="absolute top-0 inset-x-0 z-20 border-b border-white/[0.07] bg-[#030608]/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-5 md:px-8 h-12 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="brand-label">
          <Crosshair size={17} weight="bold" style={{ color: primary }} />
          <span className="font-inter text-[10px] md:text-[11px] font-bold tracking-[0.28em] uppercase text-[#FAFAFA]">
            {brandLead || "Emergent's"} <span style={{ color: primary }}>{brandAccent || "Game Room"}</span>
          </span>
        </div>
        <span className="hidden md:block" />
        {rightNode ? (
          <span data-testid="hero-topbar-right">{rightNode}</span>
        ) : dates ? (
          <span className="inline-flex items-center gap-2 border border-white/15 rounded-full px-5 py-1.5 hud text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#E8EDD9]" data-testid="hero-dates">
            <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: primary, boxShadow: `0 0 8px ${primary}` }} />
            {dates}
          </span>
        ) : null}
      </div>
    </header>
  );
}

function Modal({ open, onClose, children, testId, maxW = "max-w-2xl" }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          style={{ background: "rgba(3,6,8,0.85)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
          data-testid={`${testId}-overlay`}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className={`relative w-full ${maxW}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={testId}
          >
            <div className="tct-panel relative p-5 md:p-8 max-h-[86dvh] overflow-y-auto">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-md border border-white/15 text-[#BCCCA1] hover:text-white hover:border-[#6FF20A] transition-colors cursor-pointer"
                data-testid={`${testId}-close`}
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useCountUp(value, delay = 0, duration = 500) {
  const [n, setN] = useState(REDUCED ? value : 0);
  useEffect(() => {
    if (REDUCED) return;
    let raf;
    const timer = setTimeout(() => {
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay * 1000);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [value, delay, duration]);
  return n;
}

function Countdown({ startDate, startsIn = "Starts in", liveWord = "Live", dayLabel = "Day", primary = RED, compact = false }) {
  const calc = () => {
    const diff = new Date(startDate).getTime() - Date.now();
    if (isNaN(diff)) return null;
    if (diff <= 0) return "live";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return { d, h, m };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    setT(calc());
    const id = setInterval(() => setT(calc()), 30000);
    return () => clearInterval(id);
  }, [startDate]);
  if (!t) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <span
      className={`${compact ? "" : "mt-4"} inline-flex items-center gap-2 font-inter text-[10px] md:text-[11px] font-bold tracking-[0.28em] uppercase`}
      style={{ color: primary, textShadow: `0 0 12px ${primary}66` }}
      data-testid="hero-countdown"
    >
      {t === "live" ? (
        <span className="inline-flex items-center gap-2.5">
          <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: primary, boxShadow: `0 0 10px ${primary}` }} />
          {liveWord}
          <span className="font-chakra text-xs md:text-sm tracking-[0.1em] text-[#FAFAFA]" data-testid="hero-day">
            {dayLabel} {Math.max(1, Math.floor((Date.now() - new Date(startDate).getTime()) / 86400000) + 1)}
          </span>
        </span>
      ) : (
        <>
          {startsIn}
          <span className="font-chakra text-xs md:text-sm tracking-[0.1em]">
            {pad(t.d)}D : {pad(t.h)}H : {pad(t.m)}M
          </span>
        </>
      )}
    </span>
  );
}

function Bar({ tier, i, max, accent, top }) {
  const delay = i * 0.1;
  const px = Math.max(26, (tier.amount / max) * 160);
  const n = useCountUp(tier.amount, delay);
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center">
      <div className="w-full flex flex-col items-center justify-end" style={{ height: 196 }}>
        <span
          className="font-chakra text-[9px] md:text-[11px] whitespace-nowrap mb-1.5"
          style={{ color: accent, textShadow: top ? `0 0 10px ${accent}88` : "none" }}
        >
          ${n.toLocaleString()}
        </span>
        <motion.div
          initial={REDUCED ? false : { scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.7, delay: 0.15 + delay, ease: [0.22, 1, 0.36, 1] }}
          className="w-full rounded-t-lg relative overflow-hidden origin-bottom border border-b-0"
          style={{
            height: px,
            borderColor: top ? `${accent}99` : `${accent}55`,
            background: top
              ? `linear-gradient(to top, ${accent}20, ${accent}80)`
              : `linear-gradient(to top, ${accent}10, ${accent}55)`,
            boxShadow: top ? `0 0 26px ${accent}55` : "none",
          }}
        >
          <span
            className="absolute top-0 left-0 w-full h-[2px]"
            style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
          />
        </motion.div>
      </div>
      <span className="mt-1.5 font-inter text-[8px] md:text-[9px] font-bold text-[#BCCCA1]">{tier.count}</span>
    </div>
  );
}

function LadderChart({ data, accent, testId, unitLabel = "Conversions" }) {
  const tiers = data?.tiers || [];
  const max = Math.max(...tiers.map((t) => t.amount), 1);
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5" style={{ background: accent, boxShadow: `0 0 6px ${accent}` }} />
        <span className="font-chakra uppercase tracking-[0.12em] text-sm md:text-base" style={{ color: accent }}>
          {data?.name}
        </span>
      </div>
      <p className="mt-0.5 hud text-[8px] md:text-[9px] font-bold tracking-[0.14em] uppercase text-[#BCCCA1]/70">{data?.subtitle}</p>
      <div className="mt-3 flex items-end gap-1.5 md:gap-2 border-b border-white/10 pb-px">
        {tiers.map((t, i) => (
          <Bar key={i} tier={t} i={i} max={max} accent={accent} top={i === tiers.length - 1} />
        ))}
      </div>
      <p className="mt-2 text-center hud text-[8px] font-bold tracking-[0.2em] uppercase text-[#BCCCA1]/60">{unitLabel}</p>
    </div>
  );
}

function GeoLadderTable({ data, accent, testId }) {
  const tiers = data?.tiers || [];
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5" style={{ background: accent, boxShadow: `0 0 6px ${accent}` }} />
        <span className="font-chakra uppercase tracking-[0.12em] text-sm md:text-base" style={{ color: accent }}>{data?.name}</span>
      </div>
      <p className="mt-0.5 hud text-[8px] md:text-[9px] font-bold tracking-[0.14em] uppercase text-[#BCCCA1]/70">{data?.subtitle}</p>
      <div className="mt-3 rounded-xl border overflow-hidden" style={{ borderColor: `${accent}55` }}>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr style={{ background: "linear-gradient(180deg, #2A2168, #1B2A63)" }}>
              <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-2.5">Convs ≥</th>
              <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-2.5 text-right">Bonus</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t, i) => {
              const top = i === tiers.length - 1;
              return (
                <tr key={i} data-testid={`${testId}-row-${i + 1}`} style={{ background: top ? `${accent}1A` : i % 2 ? "rgba(255,255,255,0.02)" : "transparent", borderTop: "1px solid rgba(180,110,240,0.14)" }}>
                  <td className="font-inter text-xs md:text-sm text-[#FAFAFA] px-3 md:px-4 py-2 whitespace-nowrap">{t.count}+</td>
                  <td className="font-chakra text-sm md:text-base px-3 md:px-4 py-2 text-right whitespace-nowrap" style={{ color: accent, fontWeight: top ? 700 : 500 }}>${t.amount.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function HudPage({ variant = "v2" }) {
  const isSunset = variant === "v4";
  const isMidnight = variant === "v5" || variant === "v6" || variant === "v7";
  const FB = variant === "v1" ? FALLBACK_CONTENT_V1_HUD : variant === "v3" ? FALLBACK_CONTENT_V3 : variant === "v4" ? FALLBACK_CONTENT_V4 : variant === "v5" ? FALLBACK_CONTENT_V5 : variant === "v6" ? FALLBACK_CONTENT_V6 : variant === "v7" ? FALLBACK_CONTENT_V7 : FALLBACK_CONTENT_V2;
  const theme = THEMES[isMidnight ? "midnight" : isSunset ? "sunset" : "green"];
  const RED = theme.primary;
  const AMBER = theme.amber;
  const BODY = theme.body;
  const { ladderA, ladderB } = theme;
  const [content, setContent] = useState(FB);
  const [loaded, setLoaded] = useState(false);
  const [screen, setScreen] = useState(1);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    fetchContent(variant)
      .then((data) => { if (active) setContent({ ...FB, ...data }); })
      .catch(() => { if (active) setContent(FB); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [variant]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setPopup(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const h = content.hero || {};
  const ranks = content.ranks || {};
  const signin = content.signin || null;
  const ladders = content.ladders || {};
  const steps = content.steps || {};
  const fc = content.finalCta || {};
  const rankItems = ranks.items || [];
  const ui = content.ui || {};
  const screen2Heading = content.screen2Heading || "How to Earn";
  const breakdown = content.breakdown || null;

  const goScreen = (n) => {
    trackEvent("screen_nav", `screen-${n}`);
    setScreen(n);
  };
  const openPopup = (id) => {
    trackEvent("card_open", id);
    setPopup(id);
  };

  const cards = [
    signin
      ? {
          id: "signin", num: "01",
          title: signin.title || "Sign-in Bonus",
          teaser: signin.teaser || "Get $30 just for joining.",
          icon: <Coins size={22} weight="fill" color={AMBER} />,
        }
      : {
          id: "ranks", num: "01",
          title: ranks.title || "Leaderboard Ranks",
          teaser: ranks.teaser || "See what topping the board pays.",
          icon: <Crown size={22} weight="fill" color={AMBER} />,
        },
    {
      id: "ladders", num: "02",
      title: ladders.title || "Milestones",
      teaser: ladders.teaser || "Earn bonuses as your conversions climb.",
      icon: (
        <span className="flex items-end gap-[3px] h-5">
          <span className="w-[5px] h-2 rounded-[1px]" style={{ background: ladderA }} />
          <span className="w-[5px] h-3.5 rounded-[1px]" style={{ background: AMBER }} />
          <span className="w-[5px] h-5 rounded-[1px]" style={{ background: ladderB }} />
        </span>
      ),
    },
    ...(isMidnight
      ? []
      : [{
          id: "steps", num: "03",
          title: steps.title || "How to Claim",
          teaser: steps.teaser || "Three steps to get paid.",
          icon: <CheckCircle size={24} weight="bold" color={RED} />,
        }]),
    ...(breakdown
      ? [{
          id: "breakdown", num: isMidnight ? "03" : "04",
          title: breakdown.title || "How the #1 partner hits $10,000+",
          teaser: breakdown.teaser || "The math, stacked.",
          icon: <Trophy size={22} weight="fill" color={AMBER} />,
        }]
      : []),
  ];

  if (!loaded) {
    return (
      <div className={`relative h-[100dvh] w-full overflow-hidden tct-bg ${isMidnight ? "hud-midnight" : isSunset ? "hud-sunset" : ""}`} style={{ color: BODY }} data-testid="landing-loading">
        <div className="absolute inset-0 tct-grid pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center" role="status" aria-label="Loading">
          <span className="w-8 h-8 rounded-full border-2 border-white/15 animate-spin" style={{ borderTopColor: RED }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative h-[100dvh] w-full overflow-hidden tct-bg ${isMidnight ? "hud-midnight" : isSunset ? "hud-sunset" : ""}`} style={{ color: BODY }} data-testid="landing-page">
      {/* ambient */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 tct-grid" />
        <div className="absolute inset-0 tac-grain" />
      </div>

      {/* corner brackets */}
      <span className="tct-bracket border-t border-l z-10" style={{ top: 60, left: 16 }} />
      <span className="tct-bracket border-t border-r z-10" style={{ top: 60, right: 16 }} />
      <span className="tct-bracket border-b border-l z-10" style={{ bottom: 16, left: 16 }} />
      <span className="tct-bracket border-b border-r z-10" style={{ bottom: 16, right: 16 }} />

      <TopBar
        brandLead={h.brandLead}
        brandAccent={h.brandAccent}
        dates={isMidnight ? undefined : h.dates}
        rightNode={isMidnight && h.startDate ? <Countdown compact startDate={h.startDate} startsIn={h.startsInLabel} liveWord={h.liveWord} dayLabel={h.dayLabel} primary={RED} /> : undefined}
        primary={RED}
      />

      <AnimatePresence mode="wait">
        {screen === 1 ? (
          /* ---------- SCREEN 1 ---------- */
          <motion.section
            key="s1"
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-10 pb-12"
            data-testid="screen-1"
          >
            {/* prize imagery (desktop) */}
            <motion.img
              src="/assets/prize-trophy.png"
              alt=""
              aria-hidden="true"
              initial={false}
              animate={{ opacity: 0.7, x: 0 }}
              transition={{ duration: 0 }}
              className="hidden lg:block absolute left-[1%] bottom-[-5%] h-[56%] w-auto pointer-events-none select-none tct-shimmer-slow"
              style={{
                WebkitMaskImage: "radial-gradient(ellipse 62% 60% at 50% 48%, black 50%, transparent 78%)",
                maskImage: "radial-gradient(ellipse 62% 60% at 50% 48%, black 50%, transparent 78%)",
                filter: isSunset ? "sepia(0.85) saturate(3) hue-rotate(-12deg) brightness(1.03)" : "none",
              }}
            />
            <motion.img
              src="/assets/coins-rising.png"
              alt=""
              aria-hidden="true"
              initial={false}
              animate={{ opacity: 0.7, y: 0 }}
              transition={{ duration: 0 }}
              className="hidden lg:block absolute right-[1%] bottom-[-5%] h-[56%] w-auto pointer-events-none select-none tct-shimmer"
              style={{
                WebkitMaskImage: "radial-gradient(ellipse 62% 58% at 50% 48%, black 50%, transparent 76%)",
                maskImage: "radial-gradient(ellipse 62% 58% at 50% 48%, black 50%, transparent 76%)",
                filter: isSunset ? "sepia(0.85) saturate(3) hue-rotate(-12deg) brightness(1.03)" : "none",
              }}
            />

            {/* amber glow behind the prize */}
            <span
              className="absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2 w-[46rem] h-[24rem] max-w-[92vw] rounded-full pointer-events-none"
              style={{ background: isMidnight ? "radial-gradient(ellipse, rgba(150,80,240,0.24) 0%, rgba(255,80,190,0.13) 42%, transparent 72%)" : isSunset ? "radial-gradient(ellipse, rgba(255,176,32,0.12) 0%, rgba(255,106,44,0.06) 45%, transparent 70%)" : "radial-gradient(ellipse, rgba(255,198,61,0.10) 0%, rgba(111,242,10,0.05) 45%, transparent 70%)", filter: "blur(10px)" }}
              aria-hidden="true"
            />

            {/* 1. eyebrow pill */}
            {h.eyebrow && (
              <span
                className="inline-flex items-center gap-2 border border-white/12 rounded-full px-4 py-1.5 hud text-[9px] md:text-[10px] font-bold tracking-[0.3em] uppercase text-[#BCCCA1]"
                style={{ borderColor: "rgba(250,250,250,0.12)" }}
                data-testid="hero-eyebrow"
              >
                {h.eyebrow}
              </span>
            )}

            {isMidnight
              ? (h.dates && (
                  <span
                    className="mt-4 inline-flex items-center gap-2 border rounded-full px-5 py-1.5 hud text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#E8EDD9]"
                    style={{ borderColor: "rgba(255,255,255,0.15)" }}
                    data-testid="hero-dates-center"
                  >
                    <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: RED, boxShadow: `0 0 8px ${RED}` }} />
                    {h.dates}
                  </span>
                ))
              : (h.startDate && <Countdown startDate={h.startDate} startsIn={h.startsInLabel} liveWord={h.liveWord} dayLabel={h.dayLabel} primary={RED} />)}

            {/* 2. hook */}
            {h.hook && (
              <p
                className="mt-7 font-chakra text-base md:text-2xl"
                style={{ color: RED, textShadow: `0 0 18px ${RED}59` }}
                data-testid="hero-hook"
              >
                {h.hook}
              </p>
            )}

            {/* 3. headline */}
            <h1
              className="mt-5 font-chakra uppercase leading-[1.02] tracking-[0.02em] text-[#FAFAFA] text-4xl sm:text-6xl lg:text-7xl"
              data-testid="hero-headline"
            >
              {h.winPre || "Win up to"}
              <br />
              <span className={`text-5xl sm:text-7xl lg:text-8xl ${isMidnight ? "hud-mid-focal" : "tct-amber-pulse"}`} style={isMidnight ? undefined : { color: AMBER }} data-testid="hero-max-total">
                {h.maxTotal || "$21,000"}
              </span>
            </h1>

            {/* 5. sign-in chip */}
            {h.signinBonus && (
              <span
                className="mt-6 inline-flex items-center gap-2 rounded-md border px-4 py-1.5 hud text-[10px] md:text-[11px] font-bold tracking-[0.14em] uppercase text-[#C4C6AC]"
                style={isMidnight ? { borderColor: "rgba(255,95,200,0.5)", background: "rgba(255,95,200,0.09)", boxShadow: "0 0 22px rgba(255,95,200,0.2)", color: "#F6DCEE" } : { borderColor: "rgba(255,198,61,0.35)", background: "rgba(52,51,75,0.25)" }}
                data-testid="hero-signin-bonus"
              >
                <Plus size={11} weight="bold" style={{ color: isMidnight ? "#FF5FC8" : AMBER }} />
                {h.signinBonus}
              </span>
            )}

            {/* 6. CTA */}
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={() => goScreen(2)} className="cursor-pointer" data-testid="learn-cta">
                <span className="tct-btn group inline-flex items-center justify-center gap-2.5 font-chakra uppercase tracking-[0.12em] text-sm md:text-[15px] px-8 md:px-10 py-4">
                  {h.learnLabel || "Learn How to Earn"}
                  <CaretDoubleRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
              {isMidnight && h.loginLabel && (
                <a
                  href={content.ctaUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("cta_click", "hero-login", { label: h.loginLabel })}
                  className="hud-mid-login-cta group inline-flex items-center justify-center gap-2.5 font-chakra uppercase tracking-[0.12em] text-sm md:text-[15px] px-8 md:px-10 py-4 rounded-md border transition-all duration-300"
                  data-testid="hero-login-cta"
                >
                  {h.loginLabel}
                  <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              )}
            </div>
            {isMidnight && h.ctaNote && (
              <p className="mt-3 font-inter text-xs md:text-sm text-white text-center max-w-lg mx-auto leading-relaxed text-balance" data-testid="hero-cta-note">{h.ctaNote}</p>
            )}
          </motion.section>
        ) : (
          /* ---------- SCREEN 2 ---------- */
          <motion.section
            key="s2"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 60 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative z-10 h-full flex flex-col px-5 md:px-10 pt-[4.5rem] md:pt-20 pb-8"
            data-testid="screen-2"
          >
            <div className="flex items-center justify-between max-w-5xl w-full mx-auto shrink-0">
              <button
                onClick={() => goScreen(1)}
                className="inline-flex items-center gap-2 hud text-[10px] font-bold tracking-[0.24em] uppercase text-[#BCCCA1] border border-white/12 rounded-md px-4 py-2 hover:text-[#FAFAFA] hover:border-[#6FF20A] transition-colors cursor-pointer"
                style={{ borderColor: "rgba(250,250,250,0.12)" }}
                data-testid="back-button"
              >
                <ArrowLeft size={13} weight="bold" />
                {ui.back || "Back"}
              </button>
              <span className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.3em] uppercase text-[#BCCCA1]/50 hidden sm:block">
                {h.eyebrow}
              </span>
            </div>

            <div className="max-w-5xl w-full mx-auto mt-4 md:mt-5 shrink-0 flex items-baseline gap-4">
              <h2 className="font-chakra uppercase text-3xl md:text-5xl text-[#FAFAFA] tracking-[0.02em]" data-testid="screen-2-title">
                {screen2Heading}
              </h2>
              {ui.selectModule && (
                <span className="hidden md:inline-flex items-center gap-1 font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>
                  <CaretDoubleRight size={11} weight="bold" /> {ui.selectModule}
                </span>
              )}
            </div>

            <div className={`max-w-5xl w-full mx-auto flex-1 min-h-0 grid grid-cols-1 ${cards.length >= 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"} gap-3 md:gap-5 mt-4 md:mt-6 content-center items-stretch`}>
              {cards.map((c, ci) => (
                <motion.button
                  key={c.id}
                  initial={REDUCED ? false : { opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + ci * 0.12, duration: 0.5, ease: EASE }}
                  onClick={() => openPopup(c.id)}
                  className="group text-left cursor-pointer min-h-0 h-full"
                  data-testid={`card-${c.id}`}
                >
                  <div className="tct-card overflow-hidden p-5 md:p-6 flex flex-col h-full">
                    <span
                      className="tct-card-num font-chakra absolute right-1 top-1/2 -translate-y-1/2 leading-none select-none pointer-events-none"
                      style={{ fontSize: "clamp(6.5rem, 15vh, 9.5rem)", color: RED }}
                      aria-hidden="true"
                    >
                      {c.num}
                    </span>
                    <div className="relative flex flex-col flex-1">
                      <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg shrink-0 self-start" style={{ border: `1px solid ${RED}80`, background: `${RED}14` }}>
                        {c.icon}
                      </span>
                      <span className="block mt-4 font-inter text-[10px] font-bold tracking-[0.34em]" style={{ color: RED }}>{c.num}</span>
                      <h3 className="mt-2 font-chakra uppercase text-2xl md:text-3xl text-[#FAFAFA] leading-none" style={{ minHeight: "2em" }}>{c.title}</h3>
                      <p className="font-inter mt-2.5 text-sm text-[#BCCCA1] min-h-[2.6rem]">{c.teaser}</p>
                      <span className="mt-auto pt-5 block w-full">
                        <span className="block w-full h-px" style={{ background: `linear-gradient(90deg, ${RED}59, ${RED}0D)` }} />
                      </span>
                      <span className="mt-3 inline-flex items-center gap-1.5 font-inter text-[10px] font-bold tracking-[0.24em] uppercase" style={{ color: RED }}>
                        {ui.tapToOpen || "Tap to open"}
                        <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
            {isMidnight && h.loginLabel && (
              <div className="max-w-5xl w-full mx-auto shrink-0 mt-5 md:mt-6 flex justify-center">
                <a
                  href={content.ctaUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("cta_click", "screen2-login", { label: h.loginLabel })}
                  className="hud-mid-login-cta group inline-flex items-center justify-center gap-2.5 font-chakra uppercase tracking-[0.12em] text-sm md:text-[15px] px-8 md:px-10 py-4 rounded-md border transition-all duration-300"
                  data-testid="screen2-login-cta"
                >
                  {h.loginLabel}
                  <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ---------- POPUP: RANKS ---------- */}
      <Modal open={popup === "ranks"} onClose={() => setPopup(null)} testId="popup-ranks" maxW={rankItems.length >= 4 ? "max-w-4xl" : rankItems.length >= 3 ? "max-w-3xl" : "max-w-2xl"}>
        <span className="font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>01 · {ranks.title || "Leaderboard Ranks"}</span>
        <h3 className="mt-2 font-chakra uppercase text-3xl md:text-4xl text-[#FAFAFA]">{ranks.title || "Leaderboard Ranks"}</h3>
        <p className="font-inter mt-2 text-sm md:text-base text-[#BCCCA1]" data-testid="ranks-subtitle">{ranks.subtitle}</p>
        <div className={`mt-6 grid grid-cols-2 gap-4 ${rankItems.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : rankItems.length >= 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
          {rankItems.map((it, i) => {
            const first = i === 0;
            return (
              <div
                key={i}
                className="p-5 md:p-6 rounded-xl border"
                style={{
                  borderColor: first ? "rgba(255,198,61,0.5)" : "rgba(250,250,250,0.12)",
                  background: "rgba(250,250,250,0.03)",
                }}
                data-testid={`rank-card-${i + 1}`}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="font-inter text-[9px] font-bold tracking-[0.26em] uppercase px-2.5 py-1 rounded-sm"
                    style={{ color: first ? AMBER : "#FAFAFA", border: `1px solid ${first ? "rgba(255,198,61,0.5)" : "rgba(250,250,250,0.2)"}` }}
                  >
                    {ui.rankWord || "Rank"} {it.rank}
                  </span>
                  <span className="font-inter text-[9px] font-bold tracking-[0.18em] uppercase text-[#BCCCA1]/70">{it.label}</span>
                </div>
                {it.qualifier ? (
                  <span className="block mt-3 hud text-[9px] font-bold tracking-[0.28em] uppercase text-[#BCCCA1]/70">{it.qualifier}</span>
                ) : (
                  <span className="block mt-3 hud text-[9px] tracking-[0.28em]">&nbsp;</span>
                )}
                <span
                  className={`block font-chakra tracking-tight leading-none mt-1 whitespace-nowrap ${first ? "text-4xl md:text-5xl tct-amber-pulse" : "text-3xl md:text-4xl"}`}
                  style={{ color: first ? AMBER : "#FAFAFA" }}
                  data-testid={`rank-amount-${i + 1}`}
                >
                  <CountUp value={it.amount} prefix="$" />
                </span>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* ---------- POPUP: SIGN-IN BONUS (v1) ---------- */}
      {signin && (
        <Modal open={popup === "signin"} onClose={() => setPopup(null)} testId="popup-signin">
          <span className="font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>01 · {signin.title || "Sign-in Bonus"}</span>
          <h3 className="mt-2 font-chakra uppercase text-3xl md:text-4xl text-[#FAFAFA]">{signin.title || "Sign-in Bonus"}</h3>
          <p className="font-inter mt-2 text-sm md:text-base text-[#BCCCA1]" data-testid="signin-subtitle">{signin.subtitle}</p>
          <div
            className="mt-6 p-6 md:p-8 rounded-xl border text-center"
            style={{ borderColor: "rgba(255,198,61,0.5)", background: "rgba(250,250,250,0.03)" }}
            data-testid="signin-amount-card"
          >
            <span className="block font-inter text-[9px] font-bold tracking-[0.28em] uppercase text-[#BCCCA1]/70">Instant Bonus</span>
            <span className="block mt-2 font-chakra tracking-wide leading-none text-6xl md:text-7xl tct-amber-pulse" style={{ color: AMBER }} data-testid="signin-amount">
              <CountUp value={signin.amount || 30} />
            </span>
          </div>
          {signin.note && (
            <p className="font-inter mt-4 text-xs md:text-sm text-[#BCCCA1]/80 text-center" data-testid="signin-note">{signin.note}</p>
          )}
        </Modal>
      )}

      {/* ---------- POPUP: MILESTONES ---------- */}
      <Modal open={popup === "ladders"} onClose={() => setPopup(null)} testId="popup-ladders" maxW={ladders.premium ? "max-w-3xl" : isMidnight ? "max-w-2xl" : ladders.single ? "max-w-xl" : "max-w-3xl"}>
        <span className="font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>02 · {ladders.title || "Milestones"}</span>
        <h3 className="mt-2 font-chakra uppercase text-3xl md:text-4xl text-[#FAFAFA]">{ladders.title || "Milestones"}</h3>
        <p className="font-inter mt-2 text-sm text-[#BCCCA1] leading-relaxed" data-testid="ladders-subtitle">{ladders.subtitle}</p>
        {isMidnight && ladders.weekly ? (
          <div className="mt-5 flex flex-col gap-7">
            {/* Weekly Milestone Ladder table */}
            <div className="order-2" data-testid="weekly-ladder-table">
              <h4 className="font-chakra uppercase text-lg md:text-xl text-[#FAFAFA] tracking-[0.04em]">{ladders.weekly.title}</h4>
              <div className="mt-3 rounded-xl border overflow-hidden max-w-sm mx-auto" style={{ borderColor: "rgba(180,110,240,0.35)" }}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr style={{ background: "linear-gradient(180deg, #2A2168, #1B2A63)" }}>
                        <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-5 py-3">{ladders.weekly.colLabel || "Paid Customers"}</th>
                        <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-5 py-3 text-right whitespace-nowrap">Bonus $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ladders.weekly.rows || []).map((r, i) => {
                        const top = i === (ladders.weekly.rows || []).length - 1;
                        return (
                          <tr
                            key={i}
                            data-testid={`weekly-row-${i + 1}`}
                            style={{ background: top ? "rgba(255,95,200,0.10)" : i % 2 ? "rgba(255,255,255,0.02)" : "transparent", borderTop: "1px solid rgba(180,110,240,0.14)" }}
                          >
                            <td className="font-inter text-xs md:text-sm text-[#FAFAFA] px-3 md:px-5 py-2.5 whitespace-nowrap">{r.tier}</td>
                            <td className="font-chakra text-sm md:text-base px-3 md:px-5 py-2.5 text-right whitespace-nowrap" style={{ color: RED, fontWeight: top ? 700 : 500 }}>{r.bonus}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {ladders.weekly.rule && (
                <p className="font-inter mt-3 text-xs md:text-sm text-[#BCCCA1] italic leading-relaxed" data-testid="weekly-rule">{ladders.weekly.rule}</p>
              )}
            </div>

            {/* Daily + Streak table */}
            {ladders.daily && (
              <div className="order-1" data-testid="daily-streak-table">
                <h4 className="font-chakra uppercase text-lg md:text-xl text-[#FAFAFA] tracking-[0.04em]">{ladders.daily.title}</h4>
                {ladders.daily.note && (
                  <p className="mt-1 font-inter text-xs md:text-sm text-[#BCCCA1] italic" data-testid="daily-note">{ladders.daily.note}</p>
                )}
                <div className="mt-3 rounded-xl border overflow-hidden max-w-sm mx-auto" style={{ borderColor: "rgba(180,110,240,0.35)" }}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr style={{ background: "linear-gradient(180deg, #2A2168, #1B2A63)" }}>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-3">Item</th>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-3 text-right">Bonus acquired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ladders.daily.rows || []).map((r, i) => (
                          <tr key={i} data-testid={`daily-row-${i + 1}`} style={{ background: i % 2 ? "rgba(255,255,255,0.02)" : "transparent", borderTop: "1px solid rgba(180,110,240,0.14)" }}>
                            <td className="font-inter text-xs md:text-sm text-[#FAFAFA] px-3 md:px-4 py-2.5">{r.item}</td>
                            <td className="font-chakra text-sm md:text-base px-3 md:px-4 py-2.5 text-right whitespace-nowrap" style={{ color: RED }}>{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Growth % bonus table */}
            {ladders.growth && (
              <div className="order-3" data-testid="growth-bonus-table">
                <h4 className="font-chakra uppercase text-lg md:text-xl text-[#FAFAFA] tracking-[0.04em]">{ladders.growth.title}</h4>
                {ladders.growth.note && (
                  <p className="mt-1 font-inter text-xs md:text-sm text-[#BCCCA1] italic" data-testid="growth-note">{ladders.growth.note}</p>
                )}
                <div className="mt-3 rounded-xl border overflow-hidden max-w-sm mx-auto" style={{ borderColor: "rgba(180,110,240,0.35)" }}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr style={{ background: "linear-gradient(180deg, #2A2168, #1B2A63)" }}>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-5 py-3">Growth %</th>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-5 py-3 text-right whitespace-nowrap">Bonus $</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ladders.growth.rows || []).map((r, i) => {
                          const top = i === (ladders.growth.rows || []).length - 1;
                          return (
                            <tr key={i} data-testid={`growth-row-${i + 1}`} style={{ background: top ? "rgba(255,95,200,0.10)" : i % 2 ? "rgba(255,255,255,0.02)" : "transparent", borderTop: "1px solid rgba(180,110,240,0.14)" }}>
                              <td className="font-inter text-xs md:text-sm text-[#FAFAFA] px-3 md:px-5 py-2.5 whitespace-nowrap">{r.tier}</td>
                              <td className="font-chakra text-sm md:text-base px-3 md:px-5 py-2.5 text-right whitespace-nowrap" style={{ color: RED, fontWeight: top ? 700 : 500 }}>{r.bonus}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : ladders.single ? (
          <div className="mt-5">
            <LadderChart data={ladders.single} accent={RED} testId="ladder-single" unitLabel={ui.conversionsLabel} />
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-7">
            {isMidnight ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
                <GeoLadderTable data={ladders.premium} accent={ladderA} testId="ladder-premium" />
                <GeoLadderTable data={ladders.nonPremium} accent={ladderB} testId="ladder-nonpremium" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <LadderChart data={ladders.premium} accent={ladderA} testId="ladder-premium" unitLabel={ui.conversionsLabel} />
                <LadderChart data={ladders.nonPremium} accent={ladderB} testId="ladder-nonpremium" unitLabel={ui.conversionsLabel} />
              </div>
            )}
            {ladders.daily && (
              <div data-testid="daily-streak-table">
                <h4 className="font-chakra uppercase text-lg md:text-xl text-[#FAFAFA] tracking-[0.04em]">{ladders.daily.title}</h4>
                {ladders.daily.note && (
                  <p className="mt-1 font-inter text-xs md:text-sm text-[#BCCCA1] italic" data-testid="daily-note">{ladders.daily.note}</p>
                )}
                <div className="mt-3 rounded-xl border overflow-hidden max-w-sm mx-auto" style={{ borderColor: isMidnight ? "rgba(180,110,240,0.35)" : "rgba(255,255,255,0.18)" }}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr style={{ background: isMidnight ? "linear-gradient(180deg, #2A2168, #1B2A63)" : "rgba(255,255,255,0.06)" }}>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-3">Item</th>
                          <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#DCE2F5] px-3 md:px-4 py-3 text-right">Bonus acquired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ladders.daily.rows || []).map((r, i) => (
                          <tr key={i} data-testid={`daily-row-${i + 1}`} style={{ background: i % 2 ? "rgba(255,255,255,0.02)" : "transparent", borderTop: "1px solid rgba(180,110,240,0.14)" }}>
                            <td className="font-inter text-xs md:text-sm text-[#FAFAFA] px-3 md:px-4 py-2.5">{r.item}</td>
                            <td className="font-chakra text-sm md:text-base px-3 md:px-4 py-2.5 text-right whitespace-nowrap" style={{ color: RED }}>{r.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ---------- POPUP: HOW TO CLAIM ---------- */}
      <Modal open={popup === "steps"} onClose={() => setPopup(null)} testId="popup-steps">
        <span className="font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>03 · {steps.title || "How to Claim"}</span>
        <h3 className="mt-2 font-chakra uppercase text-3xl md:text-4xl text-[#FAFAFA]">{steps.title || "How to Claim"}</h3>
        <div className="mt-5 flex flex-col gap-3">
          {(steps.items || []).map((s, i) => (
            <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-white/[0.08] bg-white/[0.03]" data-testid={`step-${i + 1}`}>
              <span
                className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md font-chakra"
                style={{ color: RED, border: "1px solid rgba(111,242,10,0.5)", boxShadow: "0 0 10px rgba(111,242,10,0.2)" }}
              >
                {i + 1}
              </span>
              <div>
                <h4 className="font-chakra uppercase tracking-[0.1em] text-[#FAFAFA] text-sm md:text-base">{s.title}</h4>
                <p className="font-inter mt-1 text-sm text-[#BCCCA1] leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-center">
          <a
            href={content.ctaUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("cta_click", "popup-steps", { label: fc.ctaLabel })}
            className="tct-btn group inline-flex items-center justify-center gap-2.5 font-chakra uppercase tracking-[0.12em] text-sm px-8 py-3.5"
            data-testid="final-band-cta"
          >
            {fc.ctaLabel || content.ctaText}
            <CaretDoubleRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </Modal>

      {/* ---------- POPUP: BREAKDOWN (v5) ---------- */}
      {breakdown && (
        <Modal open={popup === "breakdown"} onClose={() => setPopup(null)} testId="popup-breakdown" maxW="max-w-xl">
          <span className="font-inter text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: RED }}>04 · Breakdown</span>
          <h3 className="mt-2 font-chakra uppercase text-2xl md:text-3xl text-[#FAFAFA] leading-tight">{breakdown.title || "How the #1 partner hits $10,000+"}</h3>
          {breakdown.setup && (
            <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: `${RED}55`, background: `${RED}12` }} data-testid="breakdown-setup">
              <span className="font-inter text-[9px] font-bold tracking-[0.28em] uppercase" style={{ color: RED }}>The play</span>
              <p className="font-inter mt-1 text-sm md:text-base text-[#FAFAFA] leading-relaxed">{breakdown.setup}</p>
              {breakdown.setupAside && (
                <span
                  className="inline-flex items-center mt-3 rounded-md border px-3 py-1.5 font-chakra text-sm md:text-base tracking-[0.03em]"
                  style={{ color: RED, borderColor: `${RED}66`, background: `${RED}14`, textShadow: isMidnight ? "0 0 14px rgba(165,107,240,0.45)" : "none" }}
                  data-testid="breakdown-aside"
                >
                  {breakdown.setupAside}
                </span>
              )}
            </div>
          )}
          <div className="mt-5 flex flex-col divide-y divide-white/[0.08]">
            {(breakdown.rows || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-4 py-3.5" data-testid={`breakdown-row-${i + 1}`}>
                <div>
                  <span className="font-chakra uppercase tracking-[0.06em] text-[#FAFAFA] text-base md:text-lg">{r.label}</span>
                  {r.detail && <span className="block font-inter text-xs text-[#BCCCA1]/80 mt-0.5">{r.detail}</span>}
                </div>
                <span className="font-chakra text-2xl md:text-3xl whitespace-nowrap" style={{ color: AMBER, textShadow: isMidnight ? "0 0 16px rgba(90,166,240,0.5)" : "none" }} data-testid={`breakdown-amount-${i + 1}`}>{r.amount}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-4 py-4 px-4 rounded-xl" style={isMidnight ? { background: "rgba(255,95,200,0.12)", border: "1px solid rgba(255,95,200,0.5)", boxShadow: "0 0 30px rgba(255,95,200,0.22)" } : { background: "rgba(255,176,32,0.12)", border: "1px solid rgba(255,176,32,0.5)" }} data-testid="breakdown-total">
            <span className="font-chakra uppercase tracking-[0.08em] text-[#FAFAFA] text-lg md:text-xl">{breakdown.totalLabel || "Total"}</span>
            <span className="font-chakra text-3xl md:text-4xl tct-amber-pulse whitespace-nowrap" style={{ color: AMBER }} data-testid="breakdown-total-amount">{breakdown.total || "$10,595"}</span>
          </div>
          {breakdown.perCustomer && (
            <p className="mt-3 text-center font-chakra text-sm md:text-base tracking-[0.02em]" style={{ color: RED, textShadow: isMidnight ? "0 0 14px rgba(165,107,240,0.45)" : "none" }} data-testid="breakdown-per-customer">{breakdown.perCustomer}</p>
          )}
        </Modal>
      )}
    </div>
  );
}
